/**
 * Browser events -> `ClientIntent`s, implementing the desktop input map from
 * `docs/01_GAME_DESIGN_DOCUMENT.md`:
 *
 *   Left-click        select hex / inspect      | build mode: place building
 *   Left-drag         paint a border push       | build mode: pan
 *   Right-click       clear push / deselect     | build mode: cancel placement
 *   Scroll            zoom toward the cursor    | same
 *   Middle / space+drag  pan                    | same
 *   1-9               -                         | build mode: pick from hotbar
 *   Shift+click       queue multiple pushes     | build mode: queue placements
 *
 * ## Left-drag does two things, and which one is unambiguous
 *
 * The GDD assigns left-drag to painting a border push and calls it the one core
 * combat verb, so it cannot simply become "pan the map". It does not have to:
 * **a surge can only ever originate from a hex you own** — the server rejects any
 * segment whose owning hex is not yours — so a drag that begins anywhere else
 * could never have produced a push in the first place.
 *
 * So the origin decides, with no modifier key:
 *
 *   drag starts on your own territory  -> paint a border push (unchanged)
 *   drag starts anywhere else          -> pan the map
 *
 * That leaves the verb intact exactly where it is meaningful and makes the other
 * ~90% of the screen behave like every map the player has ever used. Middle-drag
 * and space+drag still pan from anywhere, including over your own land, so there
 * is always an unconditional way to move the camera.
 *
 * This class is a shell on purpose. Every judgement call — is that a click or a
 * drag, which hexes did the drag cross, which edges of the frontier does it
 * push — lives in `pointerPath.ts`, where it is testable without a DOM.
 *
 * The controller never computes an outcome and never mutates game state. It
 * consults `deps` only to avoid emitting intents the server would certainly
 * reject (clicking unclaimable ground), and the authority for every rule
 * remains the server.
 */

import { BUILDINGS, clamp, hexKey, parseBorderSegmentId, pixelToHex } from "kngdm-shared";
import type {
  Biome,
  BorderSegmentId,
  BuildingType,
  ClientIntent,
  GodTool,
  HexCoord,
  KingdomId,
  Point,
} from "kngdm-shared";
import type { Camera } from "../render/camera";
import { classifyGesture, pointerPathToHexPath, surgeSegmentsForPath } from "./pointerPath";

// ---------------------------------------------------------------------------
// Contracts
// ---------------------------------------------------------------------------

export interface InputDeps {
  hexSize(): number;
  ownedHexKeys(): ReadonlySet<string>;
  ownerAt(coord: HexCoord): KingdomId | null;
  biomeAt(coord: HexCoord): Biome | null;
  canClaimAt(coord: HexCoord): boolean;
}

export interface InputCallbacks {
  onIntent(intent: ClientIntent): void;
  onHover(coord: HexCoord | null): void;
  onSelect(coord: HexCoord | null): void;
  onBuildModeChange(building: BuildingType | null): void;
  onGodToolChange(tool: GodTool | null): void;
  onCameraChange(): void;
  /**
   * A click the rules refused — unaffordable, wrong terrain, not your land.
   *
   * The server rejects these anyway; this exists so the *player* is told. Before
   * it, clicking an unaffordable hex produced no response whatsoever, which is
   * indistinguishable from the game being broken.
   */
  onReject(coord: HexCoord): void;
}

// ---------------------------------------------------------------------------
// Tuning
// ---------------------------------------------------------------------------

/**
 * The 1-9 hotbar, in declaration order of `BUILDINGS`. One entry for the MVP;
 * adding a building to `balance.ts` extends the hotbar with no change here,
 * which is the point of keeping the list derived rather than hand-written.
 */
const HOTBAR: readonly BuildingType[] = Object.keys(BUILDINGS) as BuildingType[];

/** God-tool hotkeys, on letters so the growing number row cannot collide. */
const GOD_TOOL_KEYS: Record<string, GodTool | undefined> = {
  q: "terraform",
  w: "fortify",
  e: "calm",
  r: "provoke",
};

/** Zoom is exponential in scroll distance so it feels the same at every scale. */
const ZOOM_PER_PIXEL = 0.0015;

/** Wheel deltas in line/page mode, normalised to something like pixels. */
const PIXELS_PER_LINE = 16;
const PIXELS_PER_PAGE = 400;

/**
 * A single wheel event with a huge delta (some trackpads, some drivers) would
 * otherwise teleport the camera from continent scale to one hex.
 */
const MAX_WHEEL_PIXELS = 240;

/**
 * How close to the viewport edge the cursor must be, in screen pixels, before the
 * camera starts following it.
 *
 * Generous enough to hit without aiming, small enough that a player working near
 * the edge of a large kingdom is not dragged around while trying to click.
 */
const EDGE_PAN_MARGIN = 48;

/** Top speed at the very edge, in screen pixels per second. */
const EDGE_PAN_MAX_SPEED = 900;

/**
 * Below this, the ramp is treated as zero.
 *
 * Without a dead zone the camera creeps by a fraction of a pixel per frame while
 * the cursor rests just inside the margin, which reads as the map drifting on its
 * own — the single most irritating thing an edge-pan can do.
 */
const EDGE_PAN_MIN_SPEED = 12;

const MOUSE_BUTTON_LEFT = 0;
const MOUSE_BUTTON_MIDDLE = 1;
const MOUSE_BUTTON_RIGHT = 2;

// ---------------------------------------------------------------------------
// Controller
// ---------------------------------------------------------------------------

interface PaintGesture {
  readonly pointerId: number;
  readonly startScreen: Point;
  readonly startedAt: number;
  /** World-space samples, fed to `pointerPathToHexPath` on release. */
  readonly worldPoints: Point[];
  /**
   * True when this gesture began somewhere a surge could not start, so a drag
   * pans instead of painting. The gesture is still tracked, because a *click*
   * there still selects a hex or places a building — only the drag differs.
   */
  readonly pansOnDrag: boolean;
}

interface PanGesture {
  readonly pointerId: number;
  lastScreen: Point;
}

export class InputController {
  private readonly canvas: HTMLCanvasElement;
  private readonly camera: Camera;
  private readonly deps: InputDeps;
  private readonly callbacks: InputCallbacks;

  private buildMode: BuildingType | null = null;
  /**
   * Armed god tool, mutually exclusive with `buildMode`.
   *
   * Kept as a second field rather than a union, because the two differ in the one
   * place that matters: a building may only be placed on land you own, while
   * `provoke` may *only* target land you do not. Collapsing them would mean
   * re-deriving that distinction at the click site anyway.
   */
  private godTool: GodTool | null = null;
  private spaceHeld = false;
  private hovered: HexCoord | null = null;
  private selected: HexCoord | null = null;
  private paint: PaintGesture | null = null;
  private pan: PanGesture | null = null;

  /**
   * Last pointer position over the canvas, or null when the cursor has left it.
   *
   * Nulled on leave and blur on purpose: a stale position parked near an edge
   * would keep the camera sliding forever while the player is in a HUD panel or
   * another window, which looks exactly like the game having lost its mind.
   */
  private edgePointer: Point | null = null;

  /**
   * Segments this client has asked the server to surge. Not game state — the
   * server owns whether the push actually exists — but the controller needs it
   * to know what an unshifted repaint replaces and what a right-click clears.
   */
  private readonly requestedSurges = new Set<BorderSegmentId>();

  /** Cached because a `getBoundingClientRect` per pointermove forces layout. */
  private canvasRect: DOMRect | null = null;

  private readonly removers: Array<() => void> = [];

  constructor(
    canvas: HTMLCanvasElement,
    camera: Camera,
    deps: InputDeps,
    callbacks: InputCallbacks,
  ) {
    this.canvas = canvas;
    this.camera = camera;
    this.deps = deps;
    this.callbacks = callbacks;

    this.onCanvas("pointerdown", this.handlePointerDown);
    this.onCanvas("pointermove", this.handlePointerMove);
    this.onCanvas("pointerup", this.handlePointerUp);
    this.onCanvas("pointercancel", this.handlePointerCancel);
    this.onCanvas("pointerleave", this.handlePointerLeave);
    this.onCanvas("wheel", this.handleWheel, { passive: false });
    this.onCanvas("contextmenu", this.handleContextMenu, { passive: false });
    // Middle-click autoscroll is a mousedown default; preventing it on
    // pointerdown alone does not reliably suppress it.
    this.onCanvas("mousedown", this.handleMouseDown, { passive: false });

    this.onWindow("keydown", this.handleKeyDown);
    this.onWindow("keyup", this.handleKeyUp);
    // Alt-tabbing away mid-gesture otherwise leaves space "held" forever.
    this.onWindow("blur", this.handleBlur);
    this.onWindow("resize", this.invalidateRect);
    this.canvas.ownerDocument.addEventListener("scroll", this.invalidateRect, true);
    this.removers.push(() =>
      this.canvas.ownerDocument.removeEventListener("scroll", this.invalidateRect, true),
    );
  }

  // -------------------------------------------------------------------------
  // Public surface
  // -------------------------------------------------------------------------

  /**
   * Advance time-based input. Call once per rendered frame with the frame's
   * elapsed milliseconds.
   *
   * Only edge panning needs this. It is a *rate*, not a response to an event, so
   * it cannot live in a pointer handler: the cursor can sit motionless in the
   * margin and the camera still has to keep moving. Driving it from the render
   * loop also means it is naturally frame-rate independent.
   */
  update(dtMs: number): void {
    this.edgePan(dtMs);
  }

  setBuildMode(building: BuildingType | null): void {
    // Guarding on equality keeps a UI that both calls this and listens to
    // `onBuildModeChange` from bouncing the change back and forth forever.
    if (building === this.buildMode) return;
    this.buildMode = building;
    if (building !== null) this.setGodTool(null);
    this.applyCursor();
    this.callbacks.onBuildModeChange(building);
  }

  setGodTool(tool: GodTool | null): void {
    if (tool === this.godTool) return;
    this.godTool = tool;
    if (tool !== null) this.setBuildMode(null);
    this.applyCursor();
    this.callbacks.onGodToolChange(tool);
  }

  /**
   * The cursor is the cheapest mode indicator there is, and it was doing nothing:
   * build mode, god-tool mode and normal mode all showed the same arrow.
   */
  private applyCursor(): void {
    this.canvas.style.cursor =
      this.buildMode !== null || this.godTool !== null ? "crosshair" : "default";
  }

  destroy(): void {
    for (const remove of this.removers) remove();
    this.removers.length = 0;
    this.paint = null;
    this.pan = null;
    this.edgePointer = null;
    this.spaceHeld = false;
  }

  // -------------------------------------------------------------------------
  // Pointer
  // -------------------------------------------------------------------------

  private readonly handlePointerDown = (event: PointerEvent): void => {
    // A second finger or a pen barrel button mid-gesture would otherwise
    // hijack the drag that is already in flight.
    if (!event.isPrimary) return;
    this.invalidateRect();

    if (event.button === MOUSE_BUTTON_RIGHT) {
      this.handleRightClick(event);
      return;
    }

    const screen = this.toScreen(event);

    // Middle-drag or space+drag pans, per the GDD input map.
    if (
      event.button === MOUSE_BUTTON_MIDDLE ||
      (event.button === MOUSE_BUTTON_LEFT && this.spaceHeld)
    ) {
      event.preventDefault();
      this.capture(event.pointerId);
      this.pan = { pointerId: event.pointerId, lastScreen: screen };
      return;
    }

    if (event.button !== MOUSE_BUTTON_LEFT) return;

    this.capture(event.pointerId);

    // Where the drag starts decides what it means. A surge can only originate on
    // land you own, so a drag beginning anywhere else is free to pan — and in
    // build mode left-drag has no meaning at all, so it always pans.
    const world = this.camera.screenToWorld(screen);
    if (this.buildMode !== null || !this.ownsHexAt(world)) {
      this.pan = { pointerId: event.pointerId, lastScreen: screen };
      // Still recorded as a gesture so that a *click* (rather than a drag)
      // continues to select or place — panning only takes over once the pointer
      // actually moves, and `classifyGesture` on release is what tells them apart.
      this.paint = {
        pointerId: event.pointerId,
        startScreen: screen,
        startedAt: performance.now(),
        worldPoints: [world],
        pansOnDrag: true,
      };
      return;
    }

    this.paint = {
      pointerId: event.pointerId,
      startScreen: screen,
      startedAt: performance.now(),
      worldPoints: [world],
      pansOnDrag: false,
    };
  };

  /**
   * Pan the camera when the cursor rests near a viewport edge.
   *
   * Speed ramps with how deep into the margin the cursor is, so a player can
   * nudge the map slowly or shove it quickly without a modifier. Both axes are
   * independent, which is what makes a corner pan diagonally.
   *
   * Suppressed while an explicit drag-pan is in flight: the player is already
   * moving the map by hand, and adding an automatic slide on top fights them.
   */
  private edgePan(dtMs: number): void {
    const pointer = this.edgePointer;
    if (pointer === null || this.pan !== null) return;
    if (!(dtMs > 0)) return;

    if (this.canvasRect === null) this.canvasRect = this.canvas.getBoundingClientRect();
    const rect = this.canvasRect;

    const dx = edgeSpeed(pointer.x, rect.width);
    const dy = edgeSpeed(pointer.y, rect.height);
    if (dx === 0 && dy === 0) return;

    // Screen pixels per second. `panBy` divides by zoom, so the map slides at the
    // same apparent speed whether zoomed to a continent or to a single hex.
    const seconds = dtMs / 1000;
    this.camera.panBy(dx * seconds, dy * seconds);
    this.callbacks.onCameraChange();
  }

  /** True when the world point sits on a hex this player owns. */
  private ownsHexAt(world: Point): boolean {
    const coord = pixelToHex(world, this.deps.hexSize());
    return this.deps.ownedHexKeys().has(hexKey(coord));
  }

  private readonly handlePointerMove = (event: PointerEvent): void => {
    const screen = this.toScreen(event);
    const world = this.camera.screenToWorld(screen);
    this.edgePointer = screen;

    // Hover is computed before panning so the highlight tracks the hex the
    // cursor is over *now*, not where the world was a frame ago.
    this.updateHover(world);

    const pan = this.pan;
    if (pan !== null && pan.pointerId === event.pointerId) {
      const dx = screen.x - pan.lastScreen.x;
      const dy = screen.y - pan.lastScreen.y;
      pan.lastScreen = screen;
      // Grab-the-world: the content follows the cursor, so the camera moves the
      // opposite way.
      this.camera.panBy(-dx, -dy);
      this.callbacks.onCameraChange();
      return;
    }

    const paint = this.paint;
    if (paint !== null && paint.pointerId === event.pointerId) {
      paint.worldPoints.push(world);
    }
  };

  private readonly handlePointerUp = (event: PointerEvent): void => {
    const pan = this.pan;
    if (pan !== null && pan.pointerId === event.pointerId) {
      this.pan = null;
      // A middle- or space-drag has no gesture riding along, so it ends here. A
      // left-drag that panned does have one, and still has to be classified: the
      // player may not have moved far enough for it to be a drag at all.
      const riding = this.paint;
      if (riding === null || riding.pointerId !== event.pointerId) {
        this.release(event.pointerId);
        return;
      }
    }

    const paint = this.paint;
    if (paint === null || paint.pointerId !== event.pointerId) return;
    this.paint = null;
    this.release(event.pointerId);

    const screen = this.toScreen(event);
    const world = this.camera.screenToWorld(screen);
    paint.worldPoints.push(world);

    const gesture = classifyGesture(
      paint.startScreen,
      screen,
      performance.now() - paint.startedAt,
    );

    if (gesture === "click") {
      this.handleLeftClick(world, event.shiftKey);
      return;
    }

    // A drag that began where no surge could start has already panned the camera;
    // there is nothing further to do. Otherwise it paints a border push, which in
    // build mode the GDD leaves empty.
    if (paint.pansOnDrag) return;
    if (this.buildMode === null) {
      this.paintSurge(paint.worldPoints, event.shiftKey);
    }
  };

  private readonly handlePointerCancel = (event: PointerEvent): void => {
    if (this.pan?.pointerId === event.pointerId) this.pan = null;
    if (this.paint?.pointerId === event.pointerId) this.paint = null;
    this.release(event.pointerId);
  };

  private readonly handlePointerLeave = (): void => {
    // Stop edge panning the moment the cursor is off the canvas — over a HUD
    // panel, or out of the window entirely.
    this.edgePointer = null;
    if (this.hovered === null) return;
    this.hovered = null;
    this.callbacks.onHover(null);
  };

  private readonly handleMouseDown = (event: MouseEvent): void => {
    if (event.button === MOUSE_BUTTON_MIDDLE) event.preventDefault();
  };

  private readonly handleContextMenu = (event: MouseEvent): void => {
    // The right button is a game verb here; the browser menu would eat it.
    event.preventDefault();
  };

  private readonly handleWheel = (event: WheelEvent): void => {
    event.preventDefault();

    const perUnit =
      event.deltaMode === 1 ? PIXELS_PER_LINE : event.deltaMode === 2 ? PIXELS_PER_PAGE : 1;
    const pixels = clamp(event.deltaY * perUnit, -MAX_WHEEL_PIXELS, MAX_WHEEL_PIXELS);

    // Scrolling up (negative deltaY) zooms in.
    this.camera.zoomAt(this.toScreen(event), Math.exp(-pixels * ZOOM_PER_PIXEL));
    this.callbacks.onCameraChange();
  };

  // -------------------------------------------------------------------------
  // Keyboard
  // -------------------------------------------------------------------------

  private readonly handleKeyDown = (event: KeyboardEvent): void => {
    // The #ui overlay owns its own text fields and sliders; a game hotkey must
    // not fire while the player is typing or dragging one.
    if (isTextEntry(event.target)) return;

    if (event.code === "Space") {
      // Prevent the page from scrolling while space is the pan modifier.
      event.preventDefault();
      this.spaceHeld = true;
      return;
    }

    if (event.key === "Escape") {
      if (this.buildMode !== null) this.setBuildMode(null);
      else if (this.godTool !== null) this.setGodTool(null);
      else this.select(null);
      return;
    }

    if (event.key.length !== 1) return;

    if (event.key >= "1" && event.key <= "9") {
      const building = HOTBAR[Number(event.key) - 1];
      if (building !== undefined) {
        // Pressing the active slot again leaves build mode, so the hotbar key is
        // its own escape hatch.
        this.setBuildMode(this.buildMode === building ? null : building);
        return;
      }
    }

    // God tools sit on the letters under the number row rather than on 7-0,
    // because the build hotbar grows into those digits as buildings are added
    // and a key that silently changes meaning is worse than an odd one.
    const tool = GOD_TOOL_KEYS[event.key.toLowerCase()];
    if (tool !== undefined) this.setGodTool(this.godTool === tool ? null : tool);
  };

  private readonly handleKeyUp = (event: KeyboardEvent): void => {
    if (event.code === "Space") this.spaceHeld = false;
  };

  private readonly handleBlur = (): void => {
    this.edgePointer = null;
    this.spaceHeld = false;
    this.pan = null;
    this.paint = null;
  };

  // -------------------------------------------------------------------------
  // Game verbs
  // -------------------------------------------------------------------------

  private handleLeftClick(world: Point, shiftKey: boolean): void {
    const coord = pixelToHex(world, this.deps.hexSize());
    if (this.deps.biomeAt(coord) === null) {
      this.select(null); // Off-map click.
      return;
    }

    if (this.buildMode !== null) {
      // Ownership is the one precondition the client can check for free; the
      // server still authorises terrain and cost.
      if (this.deps.ownedHexKeys().has(hexKey(coord))) {
        this.emit({ type: "placeBuilding", buildingType: this.buildMode, hex: coord });
      } else {
        this.callbacks.onReject(coord);
      }
      // Shift queues multiple placements by keeping the hotbar slot armed.
      if (!shiftKey) this.setBuildMode(null);
      return;
    }

    if (this.godTool !== null) {
      // `provoke` is the one tool aimed at ground you do not hold, so the
      // client-side ownership filter runs the other way for it. Everything else
      // — cost, whether there is an event to calm — the server decides.
      const owned = this.deps.ownedHexKeys().has(hexKey(coord));
      if (this.godTool === "provoke" ? !owned : owned) {
        this.emit({ type: "useGodTool", tool: this.godTool, hex: coord });
      } else {
        this.callbacks.onReject(coord);
      }
      if (!shiftKey) this.setGodTool(null);
      return;
    }

    // Default mode: every left-click inspects the hex, and an affordable,
    // adjacent, unowned one is also a claim.
    this.select(coord);
    const owned = this.deps.ownedHexKeys().has(hexKey(coord));
    if (!owned) {
      if (this.deps.canClaimAt(coord)) this.emit({ type: "claimHex", hex: coord });
      // Adjacent-but-unaffordable, or too far to reach: either way the player
      // asked for something and deserves an answer.
      else this.callbacks.onReject(coord);
    }
  }

  private handleRightClick(event: PointerEvent): void {
    event.preventDefault();

    if (this.buildMode !== null) {
      this.setBuildMode(null); // Cancel placement.
      return;
    }

    if (this.godTool !== null) {
      this.setGodTool(null);
      return;
    }

    const coord = pixelToHex(this.camera.screenToWorld(this.toScreen(event)), this.deps.hexSize());
    if (!this.clearSurgesAt(coord)) this.select(null);
  }

  private paintSurge(worldPoints: readonly Point[], shiftKey: boolean): void {
    const path = pointerPathToHexPath(worldPoints, this.deps.hexSize());
    const ownedKeys = this.deps.ownedHexKeys();
    const segments = surgeSegmentsForPath(path, ownedKeys, (coord) => this.deps.ownerAt(coord));

    // A drag that touched no frontier is a stray gesture, not an order to stand
    // down everywhere — dragging across empty land must never wipe live pushes.
    if (segments.length === 0) return;

    if (!shiftKey) {
      // Without shift the new paint *replaces* the old one. Segments common to
      // both are left alone so an overlapping repaint doesn't stutter the push.
      const kept = new Set(segments);
      for (const id of [...this.requestedSurges]) {
        if (kept.has(id)) continue;
        this.requestedSurges.delete(id);
        this.emit({ type: "clearSurge", borderSegmentId: id });
      }
    }

    for (const id of segments) {
      if (this.requestedSurges.has(id)) continue;
      this.requestedSurges.add(id);
      this.emit({ type: "surge", borderSegmentId: id });
    }
  }

  /** Clears pushes anchored on one hex. Returns whether anything was cleared. */
  private clearSurgesAt(coord: HexCoord): boolean {
    const key = hexKey(coord);
    let cleared = false;
    for (const id of [...this.requestedSurges]) {
      if (hexKey(parseBorderSegmentId(id).hex) !== key) continue;
      this.requestedSurges.delete(id);
      this.emit({ type: "clearSurge", borderSegmentId: id });
      cleared = true;
    }
    return cleared;
  }

  // -------------------------------------------------------------------------
  // Small helpers
  // -------------------------------------------------------------------------

  private emit(intent: ClientIntent): void {
    this.callbacks.onIntent(intent);
  }

  private select(coord: HexCoord | null): void {
    if (coord === null && this.selected === null) return;
    this.selected = coord;
    this.callbacks.onSelect(coord);
  }

  private updateHover(world: Point): void {
    const coord = pixelToHex(world, this.deps.hexSize());
    const next = this.deps.biomeAt(coord) === null ? null : coord;

    const same =
      next === null
        ? this.hovered === null
        : this.hovered !== null && this.hovered.q === next.q && this.hovered.r === next.r;
    if (same) return;

    this.hovered = next;
    this.callbacks.onHover(next);
  }

  /** Canvas-relative CSS pixels, which is the space the camera works in. */
  private toScreen(event: MouseEvent): Point {
    if (this.canvasRect === null) this.canvasRect = this.canvas.getBoundingClientRect();
    return {
      x: event.clientX - this.canvasRect.left,
      y: event.clientY - this.canvasRect.top,
    };
  }

  private readonly invalidateRect = (): void => {
    this.canvasRect = null;
  };

  private capture(pointerId: number): void {
    // Capture keeps move/up events coming when the drag leaves the canvas —
    // otherwise a pan that reaches the window edge sticks forever.
    try {
      this.canvas.setPointerCapture(pointerId);
    } catch {
      // Some synthetic and already-released pointers throw; input still works.
    }
  }

  private release(pointerId: number): void {
    try {
      if (this.canvas.hasPointerCapture(pointerId)) this.canvas.releasePointerCapture(pointerId);
    } catch {
      // Same as above: losing the capture is never fatal.
    }
  }

  private onCanvas<K extends keyof HTMLElementEventMap>(
    type: K,
    handler: (event: HTMLElementEventMap[K]) => void,
    options?: AddEventListenerOptions,
  ): void {
    const listener = handler as EventListener;
    this.canvas.addEventListener(type, listener, options);
    this.removers.push(() => this.canvas.removeEventListener(type, listener, options));
  }

  private onWindow<K extends keyof WindowEventMap>(
    type: K,
    handler: (event: WindowEventMap[K]) => void,
  ): void {
    const listener = handler as EventListener;
    window.addEventListener(type, listener);
    this.removers.push(() => window.removeEventListener(type, listener));
  }
}

function isTextEntry(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  const tag = target.tagName;
  return tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT";
}

/**
 * Edge-pan speed along one axis, in screen pixels per second.
 *
 * Positive means the camera moves toward the far edge. Returns 0 outside the
 * margin and inside the dead zone, so a cursor resting just inside the boundary
 * does not creep the map by fractions of a pixel per frame.
 *
 * Exported for testing: this is the whole feel of the feature in six lines, and
 * it is pure arithmetic that should not need a browser to verify.
 */
export function edgeSpeed(
  position: number,
  extent: number,
  margin = EDGE_PAN_MARGIN,
  maxSpeed = EDGE_PAN_MAX_SPEED,
  minSpeed = EDGE_PAN_MIN_SPEED,
): number {
  if (!(extent > 0) || !(margin > 0)) return 0;

  // A viewport narrower than two margins would have overlapping zones, where a
  // cursor in the middle counts as near both edges at once and the two cancel.
  const usable = Math.min(margin, extent / 2);

  let ramp = 0;
  if (position < usable) ramp = -(usable - position) / usable;
  else if (position > extent - usable) ramp = (position - (extent - usable)) / usable;
  else return 0;

  const speed = ramp * maxSpeed;
  return Math.abs(speed) < minSpeed ? 0 : speed;
}
